// Importing necessary components and icons from Material-UI library and React
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  TextField,
  Link,
  Collapse,
  Box,
  Divider,
  Tooltip,
  Button,
  Icon,styled,
  useTheme
} from "@mui/material";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteIcon from "@mui/icons-material/Delete";
import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteIssueList,
  pipelinesSelector,
} from "../../../../../store/slices/pipelines";
import _ from "lodash";
import {
  insertIssueList,
  triggerUpdateIssueList,
} from "../../../../../store/slices/pipelines";
import ConfirmModalTemplate from "../../../modals/cofirmationModalTemplate";
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { tooltipClasses } from "@mui/material/Tooltip";
import { locationsSelector } from "../../../../../store/slices/location";
// Functional component for managing and editing issue lists


const NoMaxWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
  },
});



const IssueEditDetails = ({ open, handleClose, selectedCompany }) => {
  const theme = useTheme();
  // Redux state and dispatch setup
  const { isCreateIssue } = useSelector(pipelinesSelector);
  const {issueList}=useSelector(locationsSelector)
  const [openNewCollapse, setNewOpenCollapse] = useState(false); //toggle to open new issue list text field
  const [issueName, setIssueName] = useState(""); //setup new issue list name
  const [issueNameEdit, setIssueNameEdit] = useState(""); //setup edit issue list name
  const [selectedIssue, setSelectedIssue] = useState({}); //selected issue on edit or delete
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false); //toggle for delete confirmation modal
  const dispatch = useDispatch();

  // Function to close the dialog
  const onClose = () => {
    handleClose();
  };

  // Function to close the "Add a new list" collapse
  const closeCollapse = () => {
    setIssueName("");
    setNewOpenCollapse(false);
  };

  // Function to save a new issue list
  const onSaveNewList = () => {
    const data = {
      CompanyId: selectedCompany,
      Name: issueName,
    };
    if (issueName && selectedCompany) {
      dispatch(insertIssueList(data, setIssueName));
    }
  };

  // Function to edit an existing issue list
  const onEditIssueList = () => {
    const data = {
      companyId: selectedCompany,
      name: issueNameEdit,
      issueListId: selectedIssue?.issueListId,
    };
    if (selectedIssue && issueNameEdit) {
      dispatch(triggerUpdateIssueList(data));
      setSelectedIssue({});
      setIssueName("");
    }
  };

  // Function to select an issue list for editing
  const selectIssue = (data) => {
    setSelectedIssue(data);
    setIssueNameEdit(data?.name);
  };

  // Function to clear the selected issue for editing
  const emptyEditIssue = () => {
    setSelectedIssue({});
    setIssueName("");
  };

  // Function to open the delete confirmation modal
  const handleOpenDeleteConfirmation = (data) => {
    setSelectedIssue(data);
    setOpenDeleteConfirmation(true);
  };

  // Function to delete an issue list
  const deleteIssueListApi = () => {
    if (selectedIssue?.issueListId) {
      dispatch(deleteIssueList(selectedIssue?.issueListId, selectedCompany));
      handleCloseDeleteConfirmation();
    }
  };

  // Function to close the delete confirmation modal
  const handleCloseDeleteConfirmation = (data) => {
    setSelectedIssue({});
    setOpenDeleteConfirmation(false);
  };

  // Actions to be displayed in the delete confirmation modal
  const actions = (
    <>
      <Button
        variant="outlined"
        color="secondary"
        fullWidth
        onClick={handleCloseDeleteConfirmation}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        fullWidth
        color="error"
        onClick={deleteIssueListApi}
      >
        Yes, delete issue list
      </Button>
    </>
  );

  // JSX for the main component
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            spacing={2}
          >
            <Typography variant="h6">Manage Lists</Typography>
            {!openNewCollapse && (
              <div>
                <Link
                  sx={{
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    transition: "0.1s",
                  }}
                  onClick={() => setNewOpenCollapse(true)}
                >
                  Add a new list
                </Link>
              </div>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={1}>
            {/* Section for adding a new issue list */}

            {
              <Collapse in={openNewCollapse}>
                <Stack direction={"row"} spacing={0} alignItems={"center"}>
                  <TextField
                    fullWidth
                    size="small"
                    label="List Name"
                    value={issueName}
                    onChange={(e) => setIssueName(e.target.value)}
                  />
                  {/* Save button for adding a new list */}
                  <Tooltip title="Save" arrow disableInteractive>
                    <span>
                      <IconButton
                        onClick={onSaveNewList}
                        disabled={_.isEmpty(issueName) || isCreateIssue}
                        color="primary"
                        size="small"
                      >
                        <CheckIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {/* Close button for adding a new list */}
                  <Tooltip title="Close" arrow disableInteractive>
                    <span>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={closeCollapse}
                      >
                        <CloseIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Divider sx={{ mt: 2 }} />
              </Collapse>
            }
            {/* Section for displaying existing issue lists */}
            <Stack spacing={1}>
              {_.map(_.sortBy(issueList, "name"), (it) =>
                !_.isEqual(selectedIssue, it) ? (
                  // Display existing issue list
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px",
                      paddingLeft: "10px",
                      lineHeight: 2,
                      marginTop: "10px",
                      cursor: "pointer",
                      backgroundColor: theme.palette.background.paper,
                      transition: 'background-color 0.3s',
                      borderRadius: "6px",
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <Stack direction={'row'} spacing={1} alignItems={'center'}>
                    <Typography>{it.name}</Typography>
                    <NoMaxWidthTooltip title={<Stack>
                    <Typography variant="caption" sx={{display:'inline-block'}}>Created By: {it?.createdBy}</Typography>
                    <Typography variant="caption" sx={{display:'inline-block'}}>Created On: {it?.createdOn}</Typography>
                    </Stack>} arrow disableInteractive>
                    <PersonOutlineOutlinedIcon fontSize="small"/>
                    </NoMaxWidthTooltip>
                    </Stack>
                    {/* Buttons for editing and deleting an issue list */}
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      className="hidden-button"
                    >
                      <Tooltip title="Edit Issue" arrow disableInteractive>
                        <span>
                          <IconButton
                            onClick={() => selectIssue(it)}
                            size="small"
                          >
                            <BorderColorIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete Issue" arrow disableInteractive>
                        <span>
                          <IconButton
                            onClick={() => handleOpenDeleteConfirmation(it)}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Box>
                ) : (
                  // Display input for editing an issue list
                  <Stack direction={"row"} alignItems={"center"}>
                    <TextField
                      fullWidth
                      size="small"
                      label="List Name"
                      value={issueNameEdit}
                      onChange={(e) => setIssueNameEdit(e.target.value)}
                    />
                    {/* Save button for editing an issue list */}
                    <Tooltip title="Save" arrow disableInteractive>
                      <span>
                        <IconButton
                          onClick={onEditIssueList}
                          disabled={
                            _.isEmpty(issueNameEdit) ||
                            it?.name === issueNameEdit
                          }
                          color="primary"
                          size="small"
                        >
                          <CheckIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {/* Close button for editing an issue list */}
                    <Tooltip title="Close" arrow disableInteractive>
                      <span>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={emptyEditIssue}
                        >
                          <CloseIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                )
              )}
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
      {openDeleteConfirmation && (
        <ConfirmModalTemplate
          open={openDeleteConfirmation}
          onClose={handleCloseDeleteConfirmation}
          mainText="Delete Issue"
          secondaryText="Are you sure you want to delete this Issue list?"
          actions={actions}
        />
      )}
    </>
  );
};

export default IssueEditDetails;
